import { constructQueryParamName } from './search';

/**
 * Helpers for the Topbar keyword autocomplete.
 *
 * These build an in-memory index of "facet" suggestions (categories, brands,
 * designers, condition, ...) from the runtime configuration, and match a typed
 * query against it. Everything here is synchronous and side-effect free so the
 * category/brand section of the dropdown can render instantly without hitting
 * the Marketplace API.
 */

export const SUGGESTION_CATEGORY = 'category';

// Recursively walk the category tree, producing one suggestion entry per
// category (including nested subcategories). Each entry carries the SearchPage
// query params needed to filter to that category, matching the nested
// `pub_categoryLevel1..N` scheme used in SearchPage.shared.js.
const flattenCategories = (categories, categoryKey, level, parentParams) => {
  if (!Array.isArray(categories)) {
    return [];
  }
  return categories.reduce((acc, category) => {
    const levelKey = constructQueryParamName(`${categoryKey}${level}`, 'public');
    const searchParams = { ...parentParams, [levelKey]: category.id };
    const entry = {
      key: `category:${category.id}`,
      type: SUGGESTION_CATEGORY,
      typeLabelKey: 'TopbarSearchForm.suggestionTypeCategory',
      label: category.name,
      searchParams,
    };
    const children = flattenCategories(
      category.subcategories,
      categoryKey,
      level + 1,
      searchParams
    );
    return [...acc, entry, ...children];
  }, []);
};

// Turn a single enum / multi-enum listing field into suggestion entries, one
// per enum option. Only fields that are actually indexed for search can be used
// as query filters, so we skip the rest.
const flattenListingField = fieldConfig => {
  const { key, scope, schemaType, enumOptions, filterConfig } = fieldConfig || {};
  const isEnum = schemaType === 'enum' || schemaType === 'multi-enum';
  const isIndexed = filterConfig?.indexForSearch === true;
  if (!isEnum || !isIndexed || !Array.isArray(enumOptions)) {
    return [];
  }
  const paramName = constructQueryParamName(key, scope);
  const typeLabel = filterConfig?.label || fieldConfig?.showConfig?.label || key;
  return enumOptions
    .filter(o => o && o.option != null && o.label)
    .map(o => ({
      key: `${key}:${o.option}`,
      type: key,
      typeLabel,
      label: `${o.label}`,
      searchParams: { [paramName]: `${o.option}` },
    }));
};

/**
 * Build a flat, memoizable index of facet suggestions from the config.
 *
 * @param {Object} config the resolved app configuration
 * @returns {Array<{key, type, label, searchParams, typeLabel?, typeLabelKey?}>}
 */
export const buildFacetIndex = config => {
  const categoryConfiguration = config?.categoryConfiguration;
  const categoryKey = categoryConfiguration?.key;
  const categories = categoryConfiguration?.categories;
  const categoryEntries =
    categoryKey && categories
      ? flattenCategories(categories, categoryKey, 1, {})
      : [];

  const listingFields = config?.listing?.listingFields || [];
  const fieldEntries = listingFields.reduce(
    (acc, field) => [...acc, ...flattenListingField(field)],
    []
  );

  return [...categoryEntries, ...fieldEntries];
};

// Free-text public data fields (text search schema) that we surface as chips.
// These have no enum option list, so their values are derived at runtime from
// the live suggestion results instead of from config.
const TEXT_FACET_FIELDS = [
  { key: 'brand', typeLabelKey: 'TopbarSearchForm.suggestionTypeBrand' },
  { key: 'designer', typeLabelKey: 'TopbarSearchForm.suggestionTypeDesigner' },
];

/**
 * Derive brand/designer suggestion chips from the live listing results.
 *
 * Text-schema fields (brand, designer) have no fixed option list, so we collect
 * the distinct values present on the returned listings that match the query,
 * and route each chip to a per-field text filter (e.g. { pub_brand: 'Nike' }).
 *
 * @param {Array} listings suggestion listings (each may carry brand/designer)
 * @param {String} query raw user input
 * @param {Number} limitPerField max chips per field (default 2)
 */
export const deriveTextFacetSuggestions = (listings, query, limitPerField = 2) => {
  const q = (query || '').trim().toLowerCase();
  if (q.length < 2 || !Array.isArray(listings)) {
    return [];
  }

  return TEXT_FACET_FIELDS.reduce((acc, { key, typeLabelKey }) => {
    const paramName = constructQueryParamName(key, 'public');
    const seen = new Set();
    const entries = [];

    listings.forEach(listing => {
      const value = listing?.[key];
      if (typeof value !== 'string') {
        return;
      }
      const label = value.trim();
      const lower = label.toLowerCase();
      if (!label || seen.has(lower) || !lower.includes(q)) {
        return;
      }
      seen.add(lower);
      if (entries.length < limitPerField) {
        entries.push({
          key: `${key}:${lower}`,
          type: key,
          typeLabelKey,
          label,
          searchParams: { [paramName]: label },
        });
      }
    });

    return [...acc, ...entries];
  }, []);
};

/**
 * Match a typed query against a facet index.
 * Case-insensitive; prefix matches rank above substring matches.
 *
 * @param {Array} index result of buildFacetIndex
 * @param {String} query raw user input
 * @param {Number} limit max results (default 5)
 */
export const matchFacets = (index, query, limit = 5) => {
  const q = (query || '').trim().toLowerCase();
  if (q.length < 2 || !Array.isArray(index)) {
    return [];
  }
  const scored = index.reduce((acc, entry) => {
    const label = entry.label.toLowerCase();
    const idx = label.indexOf(q);
    if (idx === -1) {
      return acc;
    }
    // Prefix matches (idx === 0) sort first.
    return [...acc, { entry, rank: idx === 0 ? 0 : 1 }];
  }, []);

  return scored
    .sort((a, b) => a.rank - b.rank)
    .slice(0, limit)
    .map(s => s.entry);
};
